// src/components/checkout/GuestOtpLogin.jsx
// OTP-based guest checkout flow — sends OTP to mobile, verifies, creates guest session
import { useState, useRef, useEffect } from 'react'
import { authApi } from '../../api/services'
import { getCsrfCookie } from '../../api/client'
import { useAuth } from '../../context/AuthContext'
import { Button, Card, CardBody } from '../common/ui'
import { Phone, Shield } from 'lucide-react'
import toast from 'react-hot-toast'

export default function GuestOtpLogin({ onSuccess }) {
  const { guestLogin } = useAuth()
  const [step, setStep]     = useState('mobile') // 'mobile' | 'otp'
  const [mobile, setMobile] = useState('')
  const [otp, setOtp]       = useState(['', '', '', '', '', ''])
  const [loading, setLoading] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const inputRefs = useRef([])

  useEffect(() => {
    if (countdown <= 0) return
    const t = setTimeout(() => setCountdown(c => c - 1), 1000)
    return () => clearTimeout(t)
  }, [countdown])

  const sendOtp = async () => {
    if (!/^[6-9]\d{9}$/.test(mobile)) {
      toast.error('Enter a valid 10-digit mobile number')
      return
    }
    setLoading(true)
    try {
      await getCsrfCookie()
      await authApi.guestSendOtp({ mobile })
      toast.success('OTP sent to your mobile')
      setStep('otp')
      setCountdown(60)
      setTimeout(() => inputRefs.current[0]?.focus(), 100)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send OTP')
    } finally { setLoading(false) }
  }

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return
    const next = [...otp]
    next[index] = value.slice(-1)
    setOtp(next)
    if (value && index < 5) inputRefs.current[index + 1]?.focus()
  }

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const verifyOtp = async () => {
    const code = otp.join('')
    if (code.length !== 6) { toast.error('Enter 6-digit OTP'); return }
    setLoading(true)
    try {
      const result = await guestLogin(mobile, code)
      toast.success(result?.message || 'Verified!')
      onSuccess?.()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid OTP')
    } finally { setLoading(false) }
  }

  // Auto-submit when all 6 digits entered
  useEffect(() => {
    if (otp.every(d => d) && step === 'otp') verifyOtp()
  }, [otp]) // eslint-disable-line

  return (
    <Card className="border-pink/20">
      <CardBody className="space-y-4">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-pink-light rounded-full mb-3">
            {step === 'mobile' ? <Phone size={22} className="text-pink" /> : <Shield size={22} className="text-pink" />}
          </div>
          <h3 className="font-display font-bold text-base">
            {step === 'mobile' ? 'Quick Checkout' : 'Verify OTP'}
          </h3>
          <p className="text-xs text-muted mt-1">
            {step === 'mobile'
              ? 'Enter your mobile number to checkout — no registration needed'
              : `Enter the 6-digit code sent to ${mobile.slice(0, 2)}****${mobile.slice(-4)}`}
          </p>
        </div>

        {step === 'mobile' ? (
          <>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted font-medium">+91</span>
              <input
                type="tel"
                value={mobile}
                onChange={e => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                placeholder="Enter 10-digit mobile"
                className="input flex-1 text-center text-base tracking-wider"
                autoFocus
                onKeyDown={e => e.key === 'Enter' && sendOtp()}
              />
            </div>
            <Button className="w-full" loading={loading} onClick={sendOtp} disabled={mobile.length !== 10}>
              Send OTP
            </Button>
          </>
        ) : (
          <>
            <div className="flex justify-center gap-2">
              {otp.map((digit, i) => (
                <input
                  key={i}
                  ref={el => inputRefs.current[i] = el}
                  type="text"
                  inputMode="numeric"
                  value={digit}
                  onChange={e => handleOtpChange(i, e.target.value)}
                  onKeyDown={e => handleOtpKeyDown(i, e)}
                  className="w-10 h-12 text-center text-lg font-bold border border-border rounded-xl focus:border-pink focus:ring-1 focus:ring-pink outline-none transition"
                  maxLength={1}
                />
              ))}
            </div>
            <Button className="w-full" loading={loading} onClick={verifyOtp}>
              Verify & Continue
            </Button>
            <div className="text-center">
              {countdown > 0 ? (
                <p className="text-xs text-muted">Resend OTP in {countdown}s</p>
              ) : (
                <button onClick={sendOtp} className="text-xs text-pink font-bold hover:underline">
                  Resend OTP
                </button>
              )}
              <button onClick={() => { setStep('mobile'); setOtp(['','','','','','']) }}
                className="text-xs text-muted hover:text-gray-700 ml-4">
                Change number
              </button>
            </div>
          </>
        )}

        <p className="text-[10px] text-muted text-center">
          By continuing, you agree to Fabrinza's Terms of Service and Privacy Policy
        </p>
      </CardBody>
    </Card>
  )
}
